"use client";

import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface AccordionItemData {
  id: string;
  title: ReactNode;
  content: ReactNode;
  meta?: ReactNode;
  defaultOpen?: boolean;
}

export function Accordion({
  items,
  type = "single",
  className,
}: {
  items: AccordionItemData[];
  type?: "single" | "multiple";
  className?: string;
}) {
  const [open, setOpen] = useState<string[]>(
    items.filter((i) => i.defaultOpen).map((i) => i.id)
  );

  const toggle = (id: string) =>
    setOpen((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      return type === "single" ? [id] : [...prev, id];
    });

  return (
    <div className={cn("divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-white", className)}>
      {items.map((item) => {
        const isOpen = open.includes(item.id);
        return (
          <div key={item.id}>
            <h3>
              <button
                type="button"
                onClick={() => toggle(item.id)}
                aria-expanded={isOpen}
                aria-controls={`panel-${item.id}`}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-cream/60 sm:px-6"
              >
                <span className="flex items-center gap-3 font-medium text-ink">
                  {item.title}
                </span>
                <span className="flex items-center gap-3">
                  {item.meta && (
                    <span className="hidden text-sm text-ink-muted sm:inline">{item.meta}</span>
                  )}
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 shrink-0 text-brand-600 transition-transform duration-300",
                      isOpen && "rotate-180"
                    )}
                  />
                </span>
              </button>
            </h3>
            <div
              id={`panel-${item.id}`}
              role="region"
              className={cn(
                "grid transition-[grid-template-rows,visibility] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                isOpen ? "visible grid-rows-[1fr]" : "invisible grid-rows-[0fr]"
              )}
            >
              <div className="overflow-hidden">
                <div className="px-5 pb-5 text-[15px] leading-relaxed text-ink-soft sm:px-6">
                  {item.content}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
